import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Eye, Play } from "lucide-react";
import type { Performer } from "@shared/schema";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import StageSelectionModal from "@/components/stage-selection-modal";

interface ConsiderationTableProps {
  performers: Performer[];
  jamNightId?: number;
  disabled?: boolean;
  forceMobileView?: boolean;
}

export default function ConsiderationTable({
  performers,
  jamNightId,
  disabled = false,
  forceMobileView = false,
}: ConsiderationTableProps) {
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedPerformer, setSelectedPerformer] = useState<Performer | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Filter to only show performers in consideration
  const considerationPerformers = performers.filter(
    (performer) => performer.status === 'consideration',
  );

  const updatePerformerMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Performer>;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/performers/${id}`,
        updates,
      );
      return response.json();
    },
    onSuccess: () => {
      if (jamNightId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/performers?jamNightId=${jamNightId}`],
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/performers"] });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update performer",
        variant: "destructive",
      });
    },
  });

  const handlePromoteToSelected = (performer: Performer) => {
    const onStagePerformers = performers.filter((p) => p.status === 'on_stage');
    const availableInstruments = performer.instruments || [];
    
    // If there are no performers on stage and only one or no instruments, 
    // directly promote to selected
    if (onStagePerformers.length === 0 && availableInstruments.length <= 1) {
      updatePerformerMutation.mutate({
        id: performer.id,
        updates: {
          status: 'selected',
          currentInstruments: availableInstruments,
          replacingPerformerId: null,
        },
      });
    } else {
      // Show the stage selection modal
      setSelectedPerformer(performer);
      setShowStageModal(true);
    }
  };

  const handleStageSelection = (
    selectedInstruments: string[],
    replacementPerformerId?: number,
  ) => {
    if (!selectedPerformer) return;

    if (replacementPerformerId) {
      const replacedPerformer = performers.find((p) => p.id === replacementPerformerId);
      if (replacedPerformer) {
        updatePerformerMutation.mutate({
          id: replacementPerformerId,
          updates: {
            status: 'pending_removal',
            replacementName: selectedPerformer.name,
          },
        });
      }
    }

    updatePerformerMutation.mutate({
      id: selectedPerformer.id,
      updates: {
        status: 'selected',
        currentInstruments: selectedInstruments,
        replacingPerformerId: replacementPerformerId || null,
      },
    });

    setSelectedPerformer(null);
  };

  const handleRemoveFromConsideration = (performer: Performer) => {
    updatePerformerMutation.mutate({
      id: performer.id,
      updates: {
        status: 'participant',
      },
    });
  };

  // Don't render if no performers are in consideration
  if (considerationPerformers.length === 0) {
    return null;
  }

  return (
    <div className="bg-yellow-100/30 backdrop-blur-xl rounded-2xl shadow-2xl border border-yellow-200/40 overflow-hidden mb-4 relative group"
      style={{
        background: 'linear-gradient(135deg, rgba(251,191,36,0.25) 0%, rgba(251,191,36,0.1) 100%)',
        boxShadow: '0 8px 32px rgba(251,191,36,0.15), inset 0 1px 0 rgba(255,255,255,0.4)',
      }}>
      {/* Liquid Glass Specular Highlight */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
          pointerEvents: 'none',
        }}
      />
      
      <div className="px-6 py-4 border-b border-yellow-200/40 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800/90 flex items-center drop-shadow-sm">
              <Eye className="h-5 w-5 mr-2 text-yellow-600/90 drop-shadow-sm" />
                Consideration
              <span className="ml-2 text-sm font-normal text-slate-600/90">
                ({considerationPerformers.length})
              </span>
            </h3>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className={forceMobileView ? "hidden" : "hidden lg:block overflow-x-auto"}>
        <table className="w-full">
          <thead className="bg-yellow-100/20 backdrop-blur-sm">
            <tr className="border-b border-yellow-200/30">
              <th className="w-1/3 px-6 py-3 text-left text-xs font-medium text-yellow-700/80 uppercase tracking-wider drop-shadow-sm">
                Name
              </th>
              <th className="w-1/3 px-6 py-3 text-left text-xs font-medium text-yellow-700/80 uppercase tracking-wider drop-shadow-sm">
                Instruments
              </th>
              <th className="w-1/3 px-6 py-3 text-right text-xs font-medium text-yellow-700/80 uppercase tracking-wider drop-shadow-sm">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white/30 backdrop-blur-sm divide-y divide-yellow-200/30">
            {considerationPerformers.map((performer) => (
              <tr
                key={performer.id}
                className="hover:bg-white/50 backdrop-blur-sm transition-all duration-300"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-lg font-medium text-slate-900/90 drop-shadow-sm">
                    {performer.name}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {performer.instruments?.map((instrument) => (
                      <Badge 
                        key={instrument} 
                        variant="outline" 
                        className="bg-yellow-100/40 backdrop-blur-sm border-yellow-300/60 text-yellow-700/90"
                      >
                        {instrument}
                      </Badge>
                    )) || <span className="text-slate-500/80">No instruments</span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2 justify-end">
                    <Button
                      size="sm"
                      onClick={() => handlePromoteToSelected(performer)}
                      disabled={disabled || updatePerformerMutation.isPending}
                      className="bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300"
                      style={{
                        boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                      }}
                    >
                      <Play className="h-3 w-3 mr-1" />
                      Select
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemoveFromConsideration(performer)}
                      disabled={disabled || updatePerformerMutation.isPending}
                      className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                      style={{
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className={forceMobileView ? "block" : "block lg:hidden"}>
        <div className="divide-y divide-yellow-200/30">
          {considerationPerformers.map((performer) => (
            <div
              key={performer.id}
              className="p-4 backdrop-blur-sm transition-all duration-300 bg-white/30"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="text-lg font-medium text-slate-900/90 drop-shadow-sm mb-1">
                    {performer.name}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-0">
                    {performer.instruments?.map((instrument) => (
                      <Badge 
                        key={instrument} 
                        variant="outline" 
                        className="text-xs bg-yellow-100/40 backdrop-blur-sm border-yellow-300/60 text-yellow-700/90"
                      >
                        {instrument}
                      </Badge>
                    )) || <span className="text-sm text-slate-500/80">No instruments</span>}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    size="sm"
                    onClick={() => handlePromoteToSelected(performer)}
                    disabled={disabled || updatePerformerMutation.isPending}
                    className="bg-green-600/80 backdrop-blur-sm text-white hover:bg-green-700/90 border border-green-500/30 shadow-lg transition-all duration-300"
                    style={{
                      boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Select
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemoveFromConsideration(performer)}
                    disabled={disabled || updatePerformerMutation.isPending}
                    className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-sm transition-all duration-300"
                    style={{
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stage Selection Modal */}
      <StageSelectionModal
        open={showStageModal}
        onOpenChange={setShowStageModal}
        performerName={selectedPerformer?.name || ""}
        availableInstruments={selectedPerformer?.instruments || []}
        onStagePerformers={performers.filter((p) => p.status === 'on_stage')}
        onConfirm={handleStageSelection}
      />
    </div>
  );
}